import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import Theme from 'core/themes/model'
import { mapVMHAConditions, VMHADisplayStatus } from './helpers'
import VmhaConditionsStatus from './VmhaConditionsStatus'
import { IClusterHostGroup } from './model'
import { isVolumeBackedVm } from '../../vms/helpers'
import { useMemo } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'

export const VMHAStatusForClusterList = ({
  value,
  item,
  loading,
}: {
  value: string
  item: IClusterHostGroup
  loading?: boolean
}) => {
  const classes = useStyles({ status: value })
  const vmhaStateText = (
    <Text variant="body2" className={classes.vmhaStatus}>
      {value}
    </Text>
  )

  // Return Loading State
  if (loading || value === VMHADisplayStatus.LOADING) {
    return (
      <Text variant="body2">
        <FontAwesomeIcon solid spin size="md">
          refresh
        </FontAwesomeIcon>{' '}
        Loading
      </Text>
    )
  }

  return (
    <Tooltip
      className={classes.vmhaStatusTooltipWrapper}
      customBody={
        <VmhaConditionsStatus
          forTooltip
          title={'VMHA Conditions'}
          conditions={mapVMHAConditions(item?.vmhaState)}
        />
      }
    >
      {vmhaStateText}
    </Tooltip>
  )
}

export const VMHAStatusCellForVMsList = ({ value, item, clusterHostGroups, blueprint }) => {
  const isVMHAEnabled = clusterHostGroups?.find((cluster) => cluster?.name === item?.clusterName)
    ?.vmHighAvailability?.enabled
  const status = useMemo(
    () =>
      isVMHAEnabled
        ? blueprint?.instanceSharedStorage || isVolumeBackedVm(item)
          ? VMHADisplayStatus.PROTECTED
          : VMHADisplayStatus.NOT_PROTECTED
        : VMHADisplayStatus.DISABLED,
    [isVMHAEnabled, blueprint?.instanceSharedStorage, item],
  )
  const classes = useStyles({ status })

  return (
    <Text variant="body2" className={classes.vmhaStatus}>
      {status}
    </Text>
  )
}

const useStyles = makeStyles<Theme, { status?: string; enabled?: boolean }>((theme: Theme) => {
  const commonStyles = {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap' as const,
  }

  return {
    marginRight: {
      marginRight: 8,
    },
    vmhaStatus: {
      ...commonStyles,
      cursor: 'pointer',
      '&:before': {
        content: '""',
        display: 'inherit',
        height: 12,
        width: 12,
        borderRadius: '50%',
        backgroundColor: ({ status }) => {
          switch (status) {
            case VMHADisplayStatus.PROTECTED:
              return theme.palette.green.main
            case VMHADisplayStatus.NOT_PROTECTED:
            case VMHADisplayStatus.DEGRADED:
              return theme.palette.orange.main
            case VMHADisplayStatus.DISABLED:
            default:
              return theme.palette.red.main
          }
        },
      },
    },
    circle: {
      ...commonStyles,
      '&:before': {
        content: '""',
        display: 'inherit',
        height: 12,
        width: 12,
        borderRadius: '50%',
        backgroundColor: ({ enabled }) =>
          enabled ? theme.palette.green.main : theme.palette.red.main,
      },
    },
    vmhaStatusTooltipWrapper: {
      width: 'fit-content',
    },
  }
})
